import type {
  SampleDefinitionFormData,
  SampleDefinitionIssueAction,
  SampleDetailType,
  GroupTarget,
  RegisteredAnimalTarget,
  PlannedAnimalTarget,
} from '@/model/SamplePlan.model';
import type { MetadataGlossaryApiId } from '@/model/Metadata.model';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

export const removeDefinition = (
  definitions: Array<SampleDefinitionFormData>,
  definitionId: SampleDefinitionFormData['api_id']
): Array<SampleDefinitionFormData> => {
  return definitions.filter((def) => def.api_id !== definitionId);
};

export const bulkRemoveDefinitions = (
  definitions: Array<SampleDefinitionFormData>,
  definitionIds: Array<SampleDefinitionFormData['api_id']>
): Array<SampleDefinitionFormData> => {
  const idsToRemove = new Set(definitionIds);
  return definitions.filter((def) => !idsToRemove.has(def.api_id));
};

export const removeInvalidMetadata = (
  definitions: Array<SampleDefinitionFormData>,
  definitionId: SampleDefinitionFormData['api_id'],
  metadataIds: Array<MetadataGlossaryApiId>
): Array<SampleDefinitionFormData> => {
  const invalidIds = new Set(metadataIds);

  return definitions.map((def) => {
    if (def.api_id !== definitionId) {
      return def;
    }

    return {
      ...def,
      metadata: def.metadata.filter((meta) => !invalidIds.has(meta.type)),
    };
  });
};

export const bulkRemoveInvalidMetadata = (
  definitions: Array<SampleDefinitionFormData>,
  definitionIds: Array<SampleDefinitionFormData['api_id']>,
  metadataIds: Array<MetadataGlossaryApiId>
): Array<SampleDefinitionFormData> => {
  const targetDefs = new Set(definitionIds);
  const invalidIds = new Set(metadataIds);

  return definitions.map((def) => {
    if (!targetDefs.has(def.api_id)) {
      return def;
    }

    return {
      ...def,
      metadata: def.metadata.filter((meta) => !invalidIds.has(meta.type)),
    };
  });
};

export const removeInvalidDetails = (
  definitions: Array<SampleDefinitionFormData>,
  definitionId: SampleDefinitionFormData['api_id'],
  detailTypes: Array<SampleDetailType>
): Array<SampleDefinitionFormData> => {
  const invalidTypes = new Set(detailTypes);

  return definitions.map((def) => {
    if (def.api_id !== definitionId) {
      return def;
    }

    return {
      ...def,
      details: def.details.filter((detail) => !invalidTypes.has(detail.type)),
    };
  });
};

export const bulkRemoveInvalidDetails = (
  definitions: Array<SampleDefinitionFormData>,
  definitionIds: Array<SampleDefinitionFormData['api_id']>,
  detailTypes: Array<SampleDetailType>
): Array<SampleDefinitionFormData> => {
  const targetDefs = new Set(definitionIds);
  const invalidTypes = new Set(detailTypes);

  return definitions.map((def) => {
    if (!targetDefs.has(def.api_id)) {
      return def;
    }

    return {
      ...def,
      details: def.details.filter((detail) => !invalidTypes.has(detail.type)),
    };
  });
};

export const removeInvalidTargets = (
  definitions: Array<SampleDefinitionFormData>,
  definitionId: SampleDefinitionFormData['api_id'],
  targetType: 'groups' | 'registered_animals' | 'planned_animals',
  targetIds: Array<GroupTarget['api_id'] | RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id']>
): Array<SampleDefinitionFormData> => {
  const invalidIds = new Set(targetIds);

  return definitions.map((def) => {
    if (def.api_id !== definitionId || def.target.type !== targetType) {
      return def;
    }

    // Type guard ensures we have a target with targets array
    const currentTarget = def.target;
    if (!('targets' in currentTarget)) {
      return def;
    }

    const filteredTargetIds = currentTarget.targets.filter((id) => !invalidIds.has(id));

    // If no targets remain, fall back to no_target
    if (filteredTargetIds.length === 0) {
      return {
        ...def,
        target: { type: 'no_target' },
      };
    }

    return {
      ...def,
      target: {
        ...currentTarget,
        targets: filteredTargetIds,
      } as typeof currentTarget,
    };
  });
};

export const bulkRemoveInvalidTargets = (
  definitions: Array<SampleDefinitionFormData>,
  definitionIds: Array<SampleDefinitionFormData['api_id']>,
  targetType: 'groups' | 'registered_animals' | 'planned_animals',
  targetIds: Array<GroupTarget['api_id'] | RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id']>
): Array<SampleDefinitionFormData> => {
  const targetDefs = new Set(definitionIds);
  const invalidIds = new Set(targetIds);

  return definitions.map((def) => {
    if (!targetDefs.has(def.api_id) || def.target.type !== targetType) {
      return def;
    }

    // Type guard ensures we have a target with targets array
    const currentTarget = def.target;
    if (!('targets' in currentTarget)) {
      return def;
    }

    const filteredTargetIds = currentTarget.targets.filter((id) => !invalidIds.has(id));

    // If no targets remain, fall back to no_target
    if (filteredTargetIds.length === 0) {
      return {
        ...def,
        target: { type: 'no_target' },
      };
    }

    return {
      ...def,
      target: {
        ...currentTarget,
        targets: filteredTargetIds,
      } as typeof currentTarget,
    };
  });
};

/**
 * Factory function to create an issue action handler.
 * Separates transformation logic from persistence mechanism.
 *
 * Behavior differs based on whether deleteCallback is provided:
 * - With deleteCallback (API context): sends only changed definitions for updates, uses delete endpoint for removes
 * - Without deleteCallback (Redux context): sends all definitions for both updates and removes
 *
 * @param updateCallback - Function to persist definitions (all or changed depending on context)
 * @param openModalCallback - Function to open sample plan modal (for 'change-sample-type', 'remove-and-edit')
 * @param deleteCallback - Optional function to delete definitions by IDs (API context only)
 * @returns Handler function that processes actions and calls appropriate callbacks
 */
export const createIssueActionHandler = (
  updateCallback: (definitions: Array<SampleDefinitionFormData>) => void | Promise<void>,
  openModalCallback: () => void,
  deleteCallback?: (definitionIds: Array<string>) => void | Promise<void>
): ((
  action: SampleDefinitionIssueAction,
  currentDefinitions: Array<SampleDefinitionFormData>
) => void | Promise<void>) => {
  const hasDeleteCallback = _notNil(deleteCallback);

  return async (action: SampleDefinitionIssueAction, currentDefinitions: Array<SampleDefinitionFormData>) => {
    let updatedDefinitions: Array<SampleDefinitionFormData>;
    let definitionsToSend: Array<SampleDefinitionFormData>;

    try {
      switch (action.type) {
        case 'remove-definition': {
          if (hasDeleteCallback && _notNil(deleteCallback)) {
            await deleteCallback([action.definition_id]);
          } else {
            updatedDefinitions = removeDefinition(currentDefinitions, action.definition_id);
            await updateCallback(updatedDefinitions);
          }
          successToast('Sample definition removed');
          break;
        }

        case 'bulk-remove-definitions': {
          if (hasDeleteCallback && _notNil(deleteCallback)) {
            // API context: use delete endpoint
            await deleteCallback(action.definition_ids);
          } else {
            // Redux context: send all remaining definitions
            updatedDefinitions = bulkRemoveDefinitions(currentDefinitions, action.definition_ids);
            await updateCallback(updatedDefinitions);
          }
          const count = action.definition_ids.length;
          successToast(`Removed ${count} sample definition${count > 1 ? 's' : ''}`);
          break;
        }

        case 'remove-invalid-metadata': {
          updatedDefinitions = removeInvalidMetadata(currentDefinitions, action.definition_id, action.metadata_ids);
          // API context: only send changed definition. Redux context: send all definitions.
          definitionsToSend = hasDeleteCallback
            ? updatedDefinitions.filter((def) => def.api_id === action.definition_id)
            : updatedDefinitions;
          await updateCallback(definitionsToSend);
          const count = action.metadata_ids.length;
          successToast(`Removed ${count} invalid metadata field${count > 1 ? 's' : ''}`);
          break;
        }

        case 'bulk-remove-invalid-metadata': {
          updatedDefinitions = bulkRemoveInvalidMetadata(
            currentDefinitions,
            action.definition_ids,
            action.metadata_ids
          );
          // API context: only send changed definitions. Redux context: send all definitions.
          if (hasDeleteCallback) {
            const changedIds = new Set(action.definition_ids);
            definitionsToSend = updatedDefinitions.filter((def) => changedIds.has(def.api_id));
          } else {
            definitionsToSend = updatedDefinitions;
          }
          await updateCallback(definitionsToSend);
          const defCount = action.definition_ids.length;
          const metaCount = action.metadata_ids.length;
          successToast(
            `Removed ${metaCount} invalid metadata field${metaCount > 1 ? 's' : ''} from ${defCount} definition${defCount > 1 ? 's' : ''}`
          );
          break;
        }

        case 'remove-invalid-details': {
          updatedDefinitions = removeInvalidDetails(currentDefinitions, action.definition_id, action.detail_types);
          // API context: only send changed definition. Redux context: send all definitions.
          definitionsToSend = hasDeleteCallback
            ? updatedDefinitions.filter((def) => def.api_id === action.definition_id)
            : updatedDefinitions;
          await updateCallback(definitionsToSend);
          const count = action.detail_types.length;
          successToast(`Removed ${count} invalid detail${count > 1 ? 's' : ''}`);
          break;
        }

        case 'bulk-remove-invalid-details': {
          updatedDefinitions = bulkRemoveInvalidDetails(currentDefinitions, action.definition_ids, action.detail_types);
          // API context: only send changed definitions. Redux context: send all definitions.
          if (hasDeleteCallback) {
            const changedIds = new Set(action.definition_ids);
            definitionsToSend = updatedDefinitions.filter((def) => changedIds.has(def.api_id));
          } else {
            definitionsToSend = updatedDefinitions;
          }
          await updateCallback(definitionsToSend);
          const defCount = action.definition_ids.length;
          const detailCount = action.detail_types.length;
          successToast(
            `Removed ${detailCount} invalid detail${detailCount > 1 ? 's' : ''} from ${defCount} definition${defCount > 1 ? 's' : ''}`
          );
          break;
        }

        case 'remove-invalid-targets': {
          updatedDefinitions = removeInvalidTargets(
            currentDefinitions,
            action.definition_id,
            action.target_type,
            action.target_ids
          );
          // API context: only send changed definition. Redux context: send all definitions.
          definitionsToSend = hasDeleteCallback
            ? updatedDefinitions.filter((def) => def.api_id === action.definition_id)
            : updatedDefinitions;
          await updateCallback(definitionsToSend);
          const count = action.target_ids.length;
          successToast(`Removed ${count} invalid target${count > 1 ? 's' : ''}`);
          break;
        }

        case 'bulk-remove-invalid-targets': {
          updatedDefinitions = bulkRemoveInvalidTargets(
            currentDefinitions,
            action.definition_ids,
            action.target_type,
            action.target_ids
          );
          // API context: only send changed definitions. Redux context: send all definitions.
          if (hasDeleteCallback) {
            const changedIds = new Set(action.definition_ids);
            definitionsToSend = updatedDefinitions.filter((def) => changedIds.has(def.api_id));
          } else {
            definitionsToSend = updatedDefinitions;
          }
          await updateCallback(definitionsToSend);
          const defCount = action.definition_ids.length;
          const targetCount = action.target_ids.length;
          successToast(
            `Removed ${targetCount} invalid target${targetCount > 1 ? 's' : ''} from ${defCount} definition${defCount > 1 ? 's' : ''}`
          );
          break;
        }

        case 'change-sample-type':
        case 'remove-and-edit': {
          openModalCallback();
          break;
        }

        case 'close-modal': {
          break;
        }
      }
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to handle sample definition issue action', {
          slug: 'sample-definition-issue-action-handler-failure',
          cause: error as Error,
          context: {
            action_type: action.type,
          },
        })
      );
      errorToast('Failed to apply the selected fix. Please try again.');
    }
  };
};
